import React, { useMemo } from "react";
import { fmtTime as fmtTimeShared } from "@/lib/time";

/**
 * TodayPulse — a single editorial sentence at the top of the calendar
 * that tells the truth about today in 5 seconds.
 *
 * Quiet days get a small celebration ("Nothing on. Take the dog the
 * long way."), normal days a calm summary, busy days an honest count.
 * Quietly intentional voice — italicised serif headline, no icons,
 * no badges. It complements (and never conflicts with) the
 * `BirthdayPlanningBanner` rendered below.
 *
 * Inputs:
 *  - `events` is the same merged events array the calendar uses
 *    (post-birthday-merge, post-filter).
 *  - We only look at today; future events are intentionally out of
 *    scope to keep the line scannable. The birthday banner already
 *    handles 30-day-ahead heads-up nudges.
 */

// Empty-day flavour pool. We pick one based on the date so the same
// quiet day gives the same line on every render of that day —
// stable, but rotates daily so families don't see "the long way" line
// for a week straight.
// Friendly self-contained lines for an empty day. Pulse renders one of
// these unmodified — no "Nothing on." prefix prepended (the prefix
// landed cold next to "Walk somewhere", reading a touch like a
// suicide note). Lead with the cheerful canonical line; the rest are
// warm rotations.
// Empty-day flavors — surfaced in the headline when there's nothing
// (or nothing left) on the calendar. We split into morning vs evening
// pools because "A whole day, blank. Lucky you." reads charming at
// breakfast and bizarre at 10pm after a busy day. Picking by time of
// day keeps the line believable at every glance.
//
// Lead with the calm Display-Mode-canonical line in each pool so the
// two surfaces feel coherent.
const EMPTY_FLAVORS_DAY = [
  "Nothing on. Pour a slow coffee.",
  "Nothing on for today, easy peasy.",
  "Nothing on. Read in bed for an extra ten.",
  "Nothing on. Cook the thing you've been meaning to.",
  "Nothing on. Call someone you haven't lately.",
  "A whole day, blank. Lucky you.",
  "Nothing on. Maybe a film tonight?",
  "A library kind of afternoon.",
  "Nothing on. Plant something. Water something.",
  "The sort of day that asks for a long bath.",
  "Nothing on. Open the windows and let the day in.",
  "Nothing on. Write a few lines, just for you.",
  "Nothing on. Sit somewhere and do absolutely nothing.",
  "Nothing on. Rearrange a small corner of your space.",
  "Nothing on. Grab a snack or cook a healthy meal.",
  "Nothing on. Tidy one small thing.",
  "Nothing on. Put on music and let it play.",
  "Nothing on. Step outside for five minutes.",
];
const EMPTY_FLAVORS_EVENING = [
  "Nothing else on. Pour a slow drink.",
  "Done for the day. Nice work.",
  "That's a wrap. Put your feet up.",
  "Quiet evening ahead.",
  "Nothing else on. Stretch out somewhere soft.",
  "Nothing else on. A film and a blanket?",
  "Nothing else on. Read a chapter of something.",
  "Evening's yours. Spend it slow.",
  "Nothing else on. Tea, a book, an early bed.",
  "Day's done. Breathe out.",
  "Nothing else on. Light a candle, dim a lamp.",
  "Nothing else on. The kitchen can wait until morning.",
  "Quiet hours. Take them.",
  "Nothing else on. A long shower, fresh sheets.",
];
const EMPTY_FLAVORS = EMPTY_FLAVORS_DAY; // back-compat for any external callers

function pickFlavor(dayKey, isEvening = false) {
  // Hash the YYYY-MM-DD string into the flavor pool. Stable per day,
  // distributed across the year. Pass `isEvening = true` after the
  // last event of the day has wrapped, so the copy switches from
  // morning-style ("Pour a slow coffee") to evening-style ("Tea, a
  // book, an early bed") — a freshly-empty headline at 10pm reads
  // weird otherwise.
  let h = 0;
  const key = `${dayKey}${isEvening ? "_pm" : ""}`;
  for (let i = 0; i < key.length; i += 1) {
    h = (h * 31 + key.charCodeAt(i)) | 0;
  }
  const pool = isEvening ? EMPTY_FLAVORS_EVENING : EMPTY_FLAVORS_DAY;
  const idx = Math.abs(h) % pool.length;
  return pool[idx];
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function startKey(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isAllDayLocal(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.getHours() === 0 && d.getMinutes() === 0;
}

function fmtTime(iso) {
  // Delegates to the shared helper so the family-level Time format
  // setting (Auto / 24h / 12h) flows through the headline copy too.
  return fmtTimeShared(iso) || "";
}

function endIsoOrInferred(ev) {
  if (ev.ends_at) return ev.ends_at;
  if (!ev.starts_at) return null;
  // Default 1h duration when no end is set — matches what conflict
  // detection uses elsewhere.
  const d = new Date(ev.starts_at);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(d.getHours() + 1);
  return d.toISOString();
}

function findOverlap(events) {
  // Returns the first (timed) overlap as { time } or null.
  // Compares pairs by [start, end). Birthdays/all-day events are
  // excluded — they overlap with everything by definition and would
  // make every day look congested.
  const timed = events
    .filter((e) => e.starts_at && !isAllDayLocal(e.starts_at) && e.kind !== "birthday")
    .map((e) => ({
      start: new Date(e.starts_at).getTime(),
      end: new Date(endIsoOrInferred(e) || e.starts_at).getTime(),
      title: e.title,
    }))
    .sort((a, b) => a.start - b.start);
  for (let i = 0; i < timed.length - 1; i += 1) {
    for (let j = i + 1; j < timed.length; j += 1) {
      if (timed[j].start < timed[i].end) {
        return {
          time: fmtTimeShared(
            new Date(timed[i].start < timed[j].start ? timed[j].start : timed[i].start),
          ),
        };
      }
    }
  }
  return null;
}

// Attendee prefix for the single-event line. Only fires when the
// event has a narrow attendee list (1 or 2 non-pet humans) — any more
// than that and "Mum, Dad, Lily and Charlie have…" reads worse than
// just the event title. An empty `member_ids` means "whole family"
// by convention, which also skips the prefix so we don't announce
// the obvious.
function attendeePrefix(ev, members) {
  const list = members || [];
  const ids = ev.member_ids || [];
  if (ids.length === 0) return "";
  const attendees = ids
    .map((id) => list.find((m) => m.id === id))
    .filter((m) => m && m.role !== "pet");
  if (attendees.length === 0) return "";
  const humans = list.filter((m) => m.role !== "pet");
  // Whole-family event — don't restate what the family already knows.
  if (humans.length > 0 && attendees.length === humans.length) return "";
  if (attendees.length === 1) return `${attendees[0].name} has `;
  if (attendees.length === 2) return `${attendees[0].name} and ${attendees[1].name} have `;
  return "";
}

function pulseSentence(events, members) {
  const key = todayKey();
  const nowMs = Date.now();

  // Birthday takes priority when a family-member birthday is within
  // 3 days — everything else can wait. The synthesised birthday
  // events carry `source: "birthday"` and `kind: "birthday"`; manual
  // "Aunt Edith's birthday" calendar entries don't match (they live
  // outside the family-member set) so they fall through to the
  // regular copy path.
  //
  //   • Today      → "Happy birthday {name}! 🎉"
  //   • Tomorrow   → "Tomorrow is {name}'s birthday 🎂"
  //   • 2-3 days   → "{N} days to {name}'s birthday 🎂"
  //
  // Multiple birthdays on the same day → joined names ("Anna & Leo").
  // Preempts the whole sentence because a birthday is the single
  // biggest thing a family wants surfaced on the dashboard.
  const todayStart = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  const byDaysOut = new Map();
  for (const e of events || []) {
    if (e.kind !== "birthday" || e.source !== "birthday") continue;
    const when = new Date(e.starts_at);
    if (Number.isNaN(when.getTime())) continue;
    when.setHours(0, 0, 0, 0);
    const daysOut = Math.round((when - todayStart) / (24 * 60 * 60 * 1000));
    if (daysOut < 0 || daysOut > 3) continue;
    if (!byDaysOut.has(daysOut)) byDaysOut.set(daysOut, []);
    // Strip the "turns N" trailer from the title so we get clean name(s).
    const namePart = (e.title || "").replace(/['']s\s+birthday.*$/i, "").trim()
                     || (e.title || "Someone");
    byDaysOut.get(daysOut).push(namePart);
  }
  // Nearest birthday wins.
  for (const daysOut of [0, 1, 2, 3]) {
    const names = byDaysOut.get(daysOut);
    if (!names || names.length === 0) continue;
    const nameList =
      names.length === 1
        ? names[0]
        : names.length === 2
          ? `${names[0]} & ${names[1]}`
          : `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
    if (daysOut === 0) {
      return `Happy birthday ${nameList}! 🎉`;
    }
    if (daysOut === 1) {
      return names.length === 1
        ? `Tomorrow is ${nameList}'s birthday 🎂`
        : `Tomorrow is ${nameList}'s birthday 🎂`;
    }
    return names.length === 1
      ? `${daysOut} days to ${nameList}'s birthday 🎂`
      : `${daysOut} days to ${nameList}'s birthdays 🎂`;
  }

  // "Still ahead today" — events that either start later today OR
  // are currently happening (started in the past but the effective
  // end is still in the future). Past-and-done events drop off so
  // the line doesn't keep shouting "Normal at 12:00 PM, plus 1 more"
  // when it's 14:30 and Normal wrapped an hour ago.
  const isStillAhead = (e) => {
    if (startKey(e.starts_at) !== key) return false;
    // Multi-day away trips are an ambient status, not a "thing"
    // happening today. They're surfaced separately (Display Mode
    // away strip) so the dashboard's "things today" count shouldn't
    // include them.
    const sKey = startKey(e.starts_at);
    const eKey = e.ends_at ? startKey(e.ends_at) : null;
    if (
      (e.kind === "travel" || e.kind === "day_trip") &&
      eKey &&
      sKey !== eKey
    ) {
      return false;
    }
    // Long-running day-context events (holidays imported from a public
    // calendar, school breaks, multi-hour work shifts) have an
    // `ends_at` hours after they started, which used to keep them in
    // the "still ahead" list all day. Once their start time has
    // passed, they belong in the calendar list below — the pulse
    // headline should stop shouting "1 Maj at 08:00 AM" when it's
    // 14:10. Threshold: anything longer than 4h that's already
    // started drops off. Short events (a 2h swim) still count as
    // in-progress and stay in the pulse until their end.
    const startMs = new Date(e.starts_at).getTime();
    const endIso = endIsoOrInferred(e);
    if (endIso) {
      const endMs = new Date(endIso).getTime();
      if (!Number.isNaN(endMs)) {
        if (endMs < nowMs) return false;
        const durationH = (endMs - startMs) / 3600000;
        if (durationH > 4 && startMs < nowMs) return false;
      }
    }
    return true;
  };
  const todays = (events || []).filter(isStillAhead);
  // Sort earliest first for the "first event" mention.
  todays.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  const weekdayLong = new Date().toLocaleDateString("en-US", { weekday: "long" });

  if (todays.length === 0) {
    // Empty-today path used to return a "Nothing else on. Pour a
    // slow drink." flavour line. The dashboard's H1 already shows
    // an "Empty diary / A clear morning / Quiet hours" greeting from
    // the GREETINGS_CAL_EMPTY pool, which says the same thing —
    // running both stacks reads as duplication. Hide TodayPulse on
    // empty days; the H1 carries the message alone.
    return null;
  }

  if (todays.length === 1) {
    const e = todays[0];
    const who = attendeePrefix(e, members);
    if (e.kind === "birthday") {
      // Birthdays are virtual all-day events — "A quiet Friday. Mor's
      // birthday" reads as a contradiction. Birthdays aren't quiet.
      // Just lead with the celebration. The cake emoji acts as the
      // sentence-ender, so we drop the full stop before it (a dot
      // immediately before an emoji reads as a typo, not punctuation).
      return `${e.title} today 🎂`;
    }
    // Public holidays, school breaks, or anything else imported with
    // `display_as: "band"` is day-context, not an appointment. The
    // title ("1 Maj", "Mors dag") carries the whole meaning — "A
    // quiet Friday" reads as a contradiction too.
    if (e.display_as === "band" || e.kind === "holiday") {
      return `${e.title} today.`;
    }
    if (isAllDayLocal(e.starts_at)) {
      return `A quiet ${weekdayLong}. ${who}${e.title}.`;
    }
    return `A quiet ${weekdayLong}. ${who}${e.title} at ${fmtTime(e.starts_at)}.`;
  }

  // 2+: the summary line gets noisy once there's more than one
  // thing ("2 things today — X at 09:00, plus 1 more.") and the
  // calendar right below already lists every item of the day. So
  // we just hide the whole TodayPulse block instead. The single-
  // item sentence above still lands nicely.
  return null;
}

export default function TodayPulse({ events, members }) {
  const sentence = useMemo(() => pulseSentence(events, members), [events, members]);
  // Null sentence means "too many things to summarise gracefully" —
  // the calendar list right below already does the job, so we hide
  // the whole block instead of pushing the page down with a noisy
  // "2 things today — X, plus 1 more." line.
  if (!sentence) return null;
  return (
    <div className="mb-1" data-testid="today-pulse">
      <div className="label-tiny text-[color:var(--ink-3)]">Today</div>
      <p
        className="font-display-italic text-2xl md:text-3xl leading-snug tracking-tight text-[color:var(--ink)] mt-1"
        data-testid="today-pulse-sentence"
      >
        {sentence}
      </p>
    </div>
  );
}
